import { useMemo } from 'react';
import { useRecoilValue } from 'recoil';
import { useActiveEditTarget } from '~/hooks/target/useActiveEditTarget';
import { useStableElements } from '~/hooks/useStableElements';
import type { RoiId } from '~/domain/changes/ids';
import type { Environment } from '~/engine/data/Environment';
import { multiSelectedRoiIdsState } from '~/state/atoms/structureSet/roiAtoms';
import { roiPreview3DOpenState } from '~/state/atoms/viewerAtoms';
import { previewDrawableRoiIds } from './previewRoiIds';

/** What the 3D preview draws: the selection in the Environment holding it. */
export interface RoiPreviewSubject {
    /** The Environment holding the selected ROIs. */
    readonly environment: Environment | undefined;
    /**
     * The structure set the selection belongs to; the scope of the live
     * refresh. Undefined while no set is active.
     */
    readonly structureSetUid: string | undefined;
    /** The structure set's ROIs, in the set's own order. */
    readonly setRoiOrder: readonly RoiId[];
    /** The active ROI, or null when nothing is selected. */
    readonly selectedRoiId: RoiId | null;
    /** Bulk-selected ROIs; empty, or two or more. */
    readonly multiSelectedRoiIds: ReadonlySet<RoiId>;
}

/**
 * The ROIs of the selection the preview could draw in an Environment, in
 * the set's own order: the selection, or the bulk selection, less
 * the ROIs that are not loaded there and the point ROIs, which have no
 * surface to march. Empty without an Environment. Cheap and purely derived.
 * Keeps its identity until its content changes, since a new list reframes the
 * preview.
 */
export function useRoiPreviewDrawableRoiIds(
    environment: Environment | undefined,
    setRoiOrder: readonly RoiId[],
    selectedRoiId: RoiId | null,
    multiSelectedRoiIds: ReadonlySet<RoiId>
): readonly RoiId[] {
    const drawableRoiIds = useMemo(
        () =>
            previewDrawableRoiIds({
                setRoiOrder,
                selectedRoiId,
                multiSelectedRoiIds,
                isDrawable: (roiId) => {
                    const roi = environment?.getRoi(roiId);
                    return roi !== undefined && !roi.isPointRoi;
                },
            }),
        [environment, setRoiOrder, selectedRoiId, multiSelectedRoiIds]
    );
    return useStableElements(drawableRoiIds);
}

/**
 * What the 3D preview shows, and whether it is shown at all: the global
 * selection in the edit target's Environment, scoped to the edit target's
 * structure set, while the preview toggle is on and the selection holds
 * something to draw. `undefined` otherwise, so a host that gets nothing
 * offers nothing. The Environment is the one that draws the active set,
 * and none while that set's dataset is not resident.
 */
export function useRoiPreviewSubject(): RoiPreviewSubject | undefined {
    const isPreviewOpen = useRecoilValue(roiPreview3DOpenState);
    const { environment, structureSet, roiId } = useActiveEditTarget();
    const multiSelectedRoiIds = useRecoilValue(multiSelectedRoiIdsState);
    const setRoiOrder = useStableElements(
        useMemo(
            () => structureSet?.roiArray.map((roi) => roi.roiId) ?? [],
            [structureSet]
        )
    );
    const drawableRoiIds = useRoiPreviewDrawableRoiIds(
        environment,
        setRoiOrder,
        roiId,
        multiSelectedRoiIds
    );
    const structureSetUid = structureSet?.dicomUid;
    return useMemo(() => {
        if (!isPreviewOpen || drawableRoiIds.length === 0) {
            return undefined;
        }
        return {
            environment,
            structureSetUid,
            setRoiOrder,
            selectedRoiId: roiId,
            multiSelectedRoiIds,
        };
    }, [
        isPreviewOpen,
        drawableRoiIds,
        environment,
        structureSetUid,
        setRoiOrder,
        roiId,
        multiSelectedRoiIds,
    ]);
}
